import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, catchError, tap } from 'rxjs/operators';
import { ENVIRONMENT, Logger } from 'shared';

export interface FeatureFlags {
  ordersEnabled: boolean;
  maintenanceMode: boolean;
}

/**
 * Service for managing feature flags
 * Loads flags from backend and provides synchronous/asynchronous access
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureFlagsService {
  protected logger = new Logger('FeatureFlagsService');
  private readonly environment: any = inject(ENVIRONMENT);
  private readonly apiUrl = this.environment?.apiUrl || 'http://localhost:3000/api';
  private featureFlags$ = new BehaviorSubject<FeatureFlags>({
    ordersEnabled: false, // Default to disabled (fail-safe for preview)
    maintenanceMode: false
  });

  private loaded = false;
  private pollHandle: ReturnType<typeof setInterval> | null = null;
  private readonly router = inject(Router);

  constructor(private http: HttpClient) {
    this.featureFlags$
      .pipe(
        map(flags => flags.maintenanceMode),
        distinctUntilChanged()
      )
      .subscribe(active => {
        // On the very first load, leave the redirect to `maintenanceGuard` —
        // navigating from the constructor while the initial navigation is still
        // pending (withDisabledInitialNavigation) leads to undefined behaviour.
        if (active && this.loaded) {
          this.redirectToMaintenance();
        }
      });
  }

  private redirectToMaintenance(): void {
    const url = this.router.url || '/';
    if (url.includes('/maintenance')) {
      return;
    }
    const seg = url.split('/').filter(Boolean)[0];
    const lang = seg && /^[a-z]{2}$/i.test(seg) ? seg : (localStorage.getItem('language') || 'en');
    this.logger.log(`Maintenance mode active => redirecting to /${lang}/maintenance`);
    this.router.navigate([`/${lang}/maintenance`]);
  }

  /**
   * Force the maintenance flag (e.g. after a 503 maintenance response).
   * Keeps other flags untouched.
   */
  setMaintenanceMode(active: boolean): void {
    const current = this.featureFlags$.value;
    if (current.maintenanceMode === active) {
      return;
    }
    this.featureFlags$.next({ ...current, maintenanceMode: active });
    this.logger.log('Maintenance mode set to:', active);
  }

  /**
   * Periodically refresh feature flags so an idle tab notices when maintenance
   * mode is toggled server-side without requiring a navigation or page reload.
   */
  startPolling(intervalMs: number = 60_000): void {
    if (this.pollHandle) {
      return;
    }
    this.pollHandle = setInterval(() => this.refresh(), intervalMs);
  }

  stopPolling(): void {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  }

  /**
   * Load feature flags from backend
   * Should be called in APP_INITIALIZER to ensure flags are loaded before app renders
   *
   * @returns Observable<FeatureFlags> that completes when flags are loaded
   */
  loadFeatureFlags(): Observable<FeatureFlags> {
    return this.http.get<any>(`${this.apiUrl}/config/feature_flags`)
      .pipe(
        map(response => ({
          ordersEnabled: response.orders_enabled === true,
          maintenanceMode: response.maintenance_mode === true
        })),
        tap(flags => {
          this.featureFlags$.next(flags);
          this.loaded = true;
          this.logger.log('Feature flags loaded:', flags);
        }),
        catchError(error => {
          this.logger.warn('Failed to load feature flags, defaulting to disabled:', error);
          const safeDefault = { ordersEnabled: false, maintenanceMode: false };
          this.featureFlags$.next(safeDefault);
          this.loaded = true;
          return of(safeDefault);
        })
      );
  }

  /**
   * Get feature flags as observable
   * Subscribe to this to react to flag changes
   *
   * @returns Observable<FeatureFlags>
   */
  getFeatureFlags(): Observable<FeatureFlags> {
    return this.featureFlags$.asObservable();
  }

  /**
   * Check if orders are enabled (synchronous)
   * Use this for immediate checks after flags are loaded
   *
   * @returns boolean true if orders are enabled
   */
  isOrdersEnabled(): boolean {
    return this.featureFlags$.value.ordersEnabled;
  }

  /**
   * Check if maintenance mode is active (synchronous)
   * Apps that opt in via routeBuilder().withMaintenance() use this to swap in the maintenance page.
   *
   * @returns boolean true if maintenance mode is active
   */
  isMaintenanceMode(): boolean {
    return this.featureFlags$.value.maintenanceMode;
  }

  /**
   * Check if feature flags have been loaded
   *
   * @returns boolean true if flags have been loaded from backend
   */
  isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Refresh feature flags from backend
   * Call this to reload flags if they may have changed
   */
  refresh(): void {
    this.loadFeatureFlags().subscribe();
  }
}
